import { Button, Icon, Tooltip } from '@timelyapp/react';
import classNames from 'classnames';
import FeatureGatedButton from 'components/FeatureGatedButton';
import MemoryDrawer from 'components/memory-drawer/MemoryDrawer';
import OnboardingHintPopover from 'components/popovers/OnboardingHintPopover';
import Surface from 'components/Surface';
import { Scrollbars } from 'components/scrollbars';
import { TAG_SELECT_ID } from 'components/TagSelect';
import { EMPTY_ARRAY } from 'constants/defaults';
import { HAS_ADDED_TIME_TO_HOUR } from 'constants/hints/onboarding';
import { MAX_HOUR_NOTE_CHARS } from 'constants/hour';
import keymap, { CLOSE_STANDALONE_HOUR_EDITOR } from 'constants/keyboard-shortcuts';
import {
  DONT_SHOW_BILLABLE_CHANGED_WARNING_AGAIN,
  PREFERRED_AUTO_NOTE_STRATEGY,
  SHOW_HOUR_EDITOR_ATTACHMENTS,
} from 'constants/user';
import { throttle } from 'lodash-es';
import React, { lazy, Suspense } from 'react';
import FontAwesome from 'react-fontawesome';
import { connect } from 'react-redux';
import { selectHourIds } from 'redux/modules/calendar.actions';
import { BatchHourModalMode, setBatchHourModalMode } from 'redux/modules/calendar.types';
import { fetchDayPropertiesSmartly } from 'redux/modules/dayProperties';
import {
  getHourStates,
  getShowHourStateDropdown,
  getShowUpgradeModalForHourStateDropdown,
  getUserSelectableHourStates,
} from 'redux/modules/hourStates';
import type { SetHourDraftSuggestedDuration } from 'redux/modules/hours.actions';
import {
  adjustHourDraftDuration,
  clearHourDraft,
  clearHourDraftDuration,
  deleteHourDraft,
  fetchLoggedHoursTotalForDraft,
  initializePermissionsAndProject,
  setHourDraftDuration,
  setHourDraftSuggestedDuration,
  setHourDraftTags,
  setHourDraftTimestamp,
  toggleHourHistoryModal,
  toggleTimerOnHourDraft,
  toggleTimestampOnHourDraft,
  unlockHourDraft,
  updateHourDraft,
  updateHourDraftNote,
} from 'redux/modules/hours.actions';
import { clearHourHistory, setAutoNoteStrategy } from 'redux/modules/hours.types';
import { fetchPinnedProjects } from 'redux/modules/pins.actions';
import { fetchInitialProjects } from 'redux/modules/projects.actions';
import { showSubscriptionNoticeModal } from 'redux/modules/subscriptions.actions';
import { dismissSuggestionId } from 'redux/modules/suggestions.actions';
import { getHasAnyConnectionWithReferenceTasks } from 'redux/modules/tic/tic.integrations.selectors';
import { initializeMemory } from 'redux/modules/timeline.actions';
import { saveHourDraftAndDisassociateMemoriesFromElsewhere } from 'redux/modules/timeline.memory.actions';
import { MEMORY_GROUP_HEADER_HEIGHT } from 'redux/modules/timeline.types';
import { saveUserProperty } from 'redux/modules/user.actions';
import { getHasHourStatesFeature, getHasTicIntegrationsSupport } from 'redux/selectors/accounts';
import { getHourEditorProps, getWeeklyCapacityProps } from 'redux/selectors/hours.draft';
import { getIsRouteCalendar } from 'redux/selectors/routing';
import { getIsUserAdmin } from 'redux/selectors/user';
import type { IState } from 'redux/state';
import { fetchForecastsForHourDraft } from 'routes/forecast/redux/modules/forecast.actions';
import NoteSection from 'routes/supercalendar/components/calendar/hour-editor/NoteSection';
import type { AutoNoteStrategy, ExternalHourLink, HourDraft, HourTimestamp } from 'types/hour';
import type { MappedActions } from 'types/redux';
import { Hotkeys } from 'utils/hotkeys';
import { getProjectColor } from 'utils/project';
import { truncateText } from 'utils/text-helper';
import { MOBILE_BREAKPOINT } from 'utils/viewport';
import styles from './HourEditor.module.scss';
import HourStateDropdown from './HourStateDropdown';
import {
  ActionSection,
  ButtonSection,
  InfoMessagesSection,
  Section,
  TagSection,
  TimeSection,
  TimestampPickerSection,
} from './hour-editor';
import MemorySection from './hour-editor/MemorySection';
import ProjectSection from './hour-editor/ProjectSection';
import { FormWrap, TimeSectionScrollSync } from './hour-editor/ScrollSyncComponents';
import SelectedHourStateSection from './hour-editor/SelectedHourStateSection';
import MemorySectionHeader from './MemorySectionHeader';

const TicTasksInHourEditor = lazy(
  () => import('routes/supercalendar/components/calendar/tic/TicTasksInHourEditor')
);

interface ExternalProps {
  standalone?: boolean;
  hideMemoriesOnHour?: boolean;
  isInPopover?: boolean;
}

const mapStateToProps = (state: IState) => ({
  ...getHourEditorProps(state),
  weeklyCapacityDetails: getWeeklyCapacityProps(state),
  isRouteCalendar: getIsRouteCalendar(state),
  states: getHourStates(state),
  hasHourStatesFeature: getHasHourStatesFeature(state),
  hasTicIntegrationsSupport: getHasTicIntegrationsSupport(state),
  hasAnyConnectionWithReferenceTasks: getHasAnyConnectionWithReferenceTasks(state),
  userSelectableStates: getUserSelectableHourStates(state),
  isAdmin: getIsUserAdmin(state),
  showUpgradeModalForHourStateDropdown: getShowUpgradeModalForHourStateDropdown(state),
  showHourStateDropdown: getShowHourStateDropdown(state),
});

type EditorProps = ReturnType<typeof mapStateToProps>;

const mapActionsToProps = {
  deleteHourDraft,
  updateHourDraft,
  updateHourDraftNote,
  setHourDraftTimestamp,
  unlockHourDraft,
  saveHourDraftAndDisassociateMemoriesFromElsewhere,
  clearHourDraft,
  setHourDraftTags,
  setHourDraftDuration,
  adjustHourDraftDuration,
  clearHourDraftDuration,
  setHourDraftSuggestedDuration,
  saveUserProperty,
  toggleTimerOnHourDraft,
  toggleTimestampOnHourDraft,
  toggleHourHistoryModal,
  selectHourIds,
  dismissSuggestionId,
  initializeMemory,
  initializePermissionsAndProject,
  fetchLoggedHoursTotalForDraft,
  fetchInitialProjects,
  fetchDayPropertiesSmartly,
  fetchPinnedProjects,
  clearHourHistory,
  setAutoNoteStrategy,
  showSubscriptionNoticeModal,
  fetchForecastsForHourDraft,
  setCopyOrMoveMode: setBatchHourModalMode,
};

type ActionProps = MappedActions<typeof mapActionsToProps>;

export type Props = ExternalProps & EditorProps & ActionProps;

interface State {
  showEstimatedDuration: boolean;
  note: string;
  scrollOffset: number;
  scrollTop: number;
  isDragging: boolean;
  showBillableChangedWarning: boolean;
  pendingTimestamp: HourTimestamp | null;
  canRequestGeneratedNote: boolean;
}

class HourEditor extends React.Component<Props, State> {
  state = {
    showEstimatedDuration: this.props.draft.estimated_duration
      ? this.props.draft.estimated_duration.total_seconds > 0
      : false,
    unlocked: false,
    note: this.props.draft.note,
    scrollOffset: 0,
    scrollTop: 0,
    isDragging: false,
    showBillableChangedWarning: false,
    pendingTimestamp: null,
    canRequestGeneratedNote: true,
  };

  static defaultProps = {
    standalone: false,
  };
  hourFormRef: React.RefObject<HTMLFormElement> = React.createRef();
  timeSectionRef: React.RefObject<HTMLDivElement> = React.createRef();

  componentDidMount() {
    this.props.fetchLoggedHoursTotalForDraft();

    // Initialize forecasts for owner of draft
    this.props.fetchForecastsForHourDraft(this.props.draft);

    // This will load project permissions and full project (if needed)
    this.props.initializePermissionsAndProject(this.props.draft.project.base.id);

    if (this.props.ui.isActingAsOtherUser && !this.props.isRouteCalendar) {
      this.props.fetchInitialProjects(this.props.draft.user.id);
    }

    if (this.props.ui.isDayLockingFeatureEnabled) {
      this.props.fetchDayPropertiesSmartly({
        dates: [this.props.draft.day],
        user_id: [this.props.draft.user.id],
      });
    }

    // NB: Classic Only
    // We need memories to offer "time on memories" suggestion.
    // loading here, in case MemorySidebar isn't open
    if (!this.props.ui.hasEnabledTic) {
      this.props.initializeMemory({ date: this.props.draft.day });
    }

    const scrollOffset = this.hourFormRef?.current?.clientHeight || 0;
    if (this.state.scrollOffset !== scrollOffset) {
      this.setState({ scrollOffset });
    }

    const autoNote = this.props.draft._suggestedNote;
    if (!this.props.draft.id && !this.state.note && !!autoNote) {
      this.updateNote(autoNote);
    }
  }

  componentDidUpdate(prevProps: Props, _prevState: State) {
    const { draft, hourDraftIsBasedOn, ui } = this.props;

    if (
      !this.props.ui.dontShowBillableChangedWarningAgain &&
      prevProps.draft.project.base.id !== draft.project.base.id &&
      prevProps.draft.project.fullProject?.billable &&
      draft.project.fullProject?.billable &&
      !prevProps.draft.billable &&
      draft.billable
    ) {
      this.setState({ showBillableChangedWarning: true });
    }

    const scrollOffset = this.hourFormRef?.current?.clientHeight || 0;
    if (
      this.state.scrollOffset !== scrollOffset ||
      draft.project.base.id !== prevProps.draft.project.base.id
    ) {
      this.setState({ scrollOffset });
    }

    const hasNoteChangedExternally =
      ui.isMaybeChangedDueToPusherUpdate &&
      hourDraftIsBasedOn?.note !== this.state.note &&
      this.props.draft.note &&
      this.props.draft._pusherUpdatedAt !== prevProps.draft._pusherUpdatedAt;

    if (hasNoteChangedExternally) {
      if (this.props.hourDraftIsBasedOn) {
        this.updateNote(this.props.hourDraftIsBasedOn.note);
      }
    }

    const hasNoteGenerationDependenciesChanged =
      ui.isMaybeChangedDueToPusherUpdate ||
      hourDraftIsBasedOn?.note !== prevProps.hourDraftIsBasedOn?.note ||
      this.props.draft.entry_ids.length !== prevProps.draft.entry_ids.length ||
      this.props.draft.project.base.id !== prevProps.draft.project.base.id ||
      this.props.draft.duration !== prevProps.draft.duration;

    const autofillEnabled = !!(
      ui.timelineHasMemoriesWithinTimestampRanges && hasNoteGenerationDependenciesChanged
    );

    if (autofillEnabled) {
      this.setState({
        canRequestGeneratedNote: true,
      });
    }

    const hasEstDurationChangedExternally =
      ui.isMaybeChangedDueToPusherUpdate &&
      hourDraftIsBasedOn?.estimated_duration.total_seconds !==
        prevProps.hourDraftIsBasedOn?.estimated_duration.total_seconds;

    if (hasEstDurationChangedExternally) {
      this.setState({
        showEstimatedDuration: !!hourDraftIsBasedOn?.estimated_duration.total_seconds,
      });
    }

    // When a Forecast is chosen, setHourDraftForecastId may set the planned time,
    // and local state needs to be manually set for UI to respond correctly
    const hasPlannedTimeTurnedOn = !!ui.hasPlannedTime && !prevProps.ui.hasPlannedTime;
    if (hasPlannedTimeTurnedOn) this.setState({ showEstimatedDuration: true });

    if (draft.project.base.id !== prevProps.draft.project.base.id) {
      this.props.initializePermissionsAndProject(draft.project.base.id);
    }

    this.considerUpdatingNote(prevProps);

    this.focusNextField(prevProps, _prevState);
  }

  focusNextField = (prevProps: Props, _prevState: State) => {
    const { ui, requiredTagIds } = this.props;

    // focus fields based on progress in the form
    if (ui.hasSelectedProject && !prevProps.ui.hasSelectedProject && requiredTagIds.length > 0) {
      // focus tag select field once the project has been selected and there are required tags
      const tagSelectButton = document.getElementById(TAG_SELECT_ID) as HTMLElement | null;
      tagSelectButton?.click();
    }
  };

  private considerUpdatingNote(prevProps: Props) {
    const suggestedNote = this.props.draft._suggestedNote || '';
    const prevSuggestedNote = prevProps.draft._suggestedNote || '';
    const suggestedNoteHasChanged = suggestedNote !== prevSuggestedNote;
    const hasChangedAutofill = this.props.ui.autoNoteStrategy !== prevProps.ui.autoNoteStrategy;

    // Only auto-update notes for new drafts or explicit strategy changes
    const isNewDraft = !this.props.draft.id;
    const shouldUpdate = (isNewDraft && suggestedNoteHasChanged) || hasChangedAutofill;

    if (shouldUpdate) {
      this.updateNote(suggestedNote);
    }
  }

  handleAutoNoteStrategyChange = (strategy: AutoNoteStrategy, setAsDefault?: boolean) => {
    this.props.setAutoNoteStrategy(strategy);
    if (setAsDefault) {
      this.props.saveUserProperty(PREFERRED_AUTO_NOTE_STRATEGY, strategy);
    }
    // TODO: Commented out to temporarily allow generated note to be requested again
    // const generatedSelected = strategy === 'generated';
    // this.setState({
    //   canRequestGeneratedNote: !generatedSelected,
    // });
  };

  handleNoteChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value || '';
    this.updateNote(value, true);
  };

  updateNote = (value: string, throttle = false) => {
    // Keeping note in component state is needed to keep the cursor from jumping inside the textbox.
    // See https://github.com/facebook/react/issues/955 for more info
    this.setState({ note: truncateText(value, MAX_HOUR_NOTE_CHARS) }, () => {
      if (throttle) {
        this.throttledHandleNoteChanged(value);
      } else {
        this.props.updateHourDraftNote(value);
      }
    });
  };

  // Note for future tweaking:
  // This workaround wasn't needed when "SuperCalendar" was first introduced, so this performance
  // issue can be solved better/higher up (start investigating selectors that re-run when they shouldn't)
  throttledHandleNoteChanged = throttle(note => this.props.updateHourDraftNote(note), 100, {
    leading: true,
    trailing: true,
  });

  handleDurationChanged = (hours: number, minutes: number) => {
    this.props.saveUserProperty(HAS_ADDED_TIME_TO_HOUR);
    this.props.setHourDraftDuration({ hours, minutes, section: 'logged' });
  };
  handleEstimatedDurationChanged = (hours: number, minutes: number) => {
    this.props.saveUserProperty(HAS_ADDED_TIME_TO_HOUR);
    this.props.setHourDraftDuration({ hours, minutes, section: 'estimated' });
  };

  handleUnlock = (event: React.SyntheticEvent) => {
    event.preventDefault(); // no submit
    this.props.unlockHourDraft();
  };

  handleToggleEstimatedTime = () => {
    // If we're hiding estimated time, also clear it's values:
    if (this.state.showEstimatedDuration === true) {
      this.props.clearHourDraftDuration('estimated_duration');
    }
    this.setState({ showEstimatedDuration: !this.state.showEstimatedDuration });
  };

  handleShowCopyOrMoveModal = (mode: BatchHourModalMode) => {
    this.props.setCopyOrMoveMode(mode);
    this.props.selectHourIds([this.props.draft.id || -1]);
  };

  handleShowCopyModal = () => {
    this.handleShowCopyOrMoveModal(BatchHourModalMode.Copy);
  };

  handleShowMoveModal = () => {
    this.handleShowCopyOrMoveModal(BatchHourModalMode.Move);
  };

  handleSetMemoryDuration = (payload: SetHourDraftSuggestedDuration) => {
    this.props.saveUserProperty(HAS_ADDED_TIME_TO_HOUR);
    this.props.setHourDraftSuggestedDuration(payload);
  };

  handleSubmitForm = (event: React.FormEvent, partialDraft?: Partial<HourDraft>) => {
    event.preventDefault();
    if (this.props.ui.isSaveDisabled) {
      return;
    }
    // Specifically save note from local state, since draft.note might not be updated when submitting with [ENTER]
    this.props.saveHourDraftAndDisassociateMemoriesFromElsewhere(
      { note: this.state.note, ...partialDraft },
      { clearDraft: true }
    );
  };

  handleSubmitAndCompleteForm = (event: React.FormEvent) => {
    this.handleSubmitForm(event, { forecast_completed_at: new Date().toISOString() });
  };

  handleSubmitNoteSection = (event: React.KeyboardEvent<unknown>) => {
    event.preventDefault();
    if (this.props.ui.isSaveDisabled) {
      return;
    }
    // Specifically save note from local state, since draft.note might not be updated when submitting with [ENTER]
    this.props.saveHourDraftAndDisassociateMemoriesFromElsewhere(
      { note: this.state.note },
      { clearDraft: true }
    );
  };

  handleToggleMemories = () => {
    this.props.saveUserProperty(SHOW_HOUR_EDITOR_ATTACHMENTS, !this.props.ui.showMemories);
  };

  handleRejectSuggestion = () => {
    this.props.clearHourDraft();
    const suggestionId = this.props.draft.suggestion_id;
    if (suggestionId != null) {
      this.props.dismissSuggestionId(suggestionId);
    }
  };

  handleToggleBillable = () => {
    const { draft, updateHourDraft, states } = this.props;
    let state = draft.state;
    let locked = draft.locked;
    if (draft.billable) {
      if (draft.state?.billed && draft.state?.locked) {
        state = Object.values(states).find(s => !s.billed && s.locked) || null;
      } else if (draft.state?.billed) {
        state = null;
        locked = false;
      }
    }

    updateHourDraft({
      billable: !draft.billable,
      state: state,
      locked: locked,
    });
  };

  handleCloseBillableChangedWarning = (dontShowAgain: boolean) => {
    if (dontShowAgain) {
      this.props.saveUserProperty(DONT_SHOW_BILLABLE_CHANGED_WARNING_AGAIN, true);
    }
    this.setState({ showBillableChangedWarning: false });
  };

  handleChangeState = (stateIds: Array<number> | null) => {
    const { draft, states } = this.props;
    const newState = Array.isArray(stateIds) ? states[stateIds[0]] : null;
    this.props.updateHourDraft({
      state: newState,
      billed: newState === null || newState.id === 0 ? false : draft.billed,
    });
  };

  renderTimeSection = () => {
    const {
      hints,
      draft,
      ui,
      standalone,
      suggestedDurations,
      durationForMode,
      weeklyCapacityDetails,
    } = this.props;
    const showHint = hints?.TimeSection.showHint;

    // https://linear.app/timelyapp/issue/TT-1941/remove-quick-adjustments-from-the-entry-editor
    const finalSuggestedDurations =
      ui.hasEnabledTic && suggestedDurations.some(d => d.isBasedOnMemories)
        ? EMPTY_ARRAY
        : suggestedDurations;

    const section = (
      <div style={{ backgroundColor: '#eef1f2' }} ref={this.timeSectionRef}>
        <TimeSection
          standalone={standalone}
          ui={ui}
          suggesterId={draft.suggester_id ? draft.suggester_id : draft.creator_id}
          timerStartedOn={draft.timer_started_on}
          backgroundColor={`#${getProjectColor(draft.project.base)}`}
          estimatedDuration={draft.estimated_duration}
          duration={durationForMode}
          disabled={ui.isLocked || ui.isDayLocked}
          onChange={this.handleDurationChanged}
          onChangeEstimated={this.handleEstimatedDurationChanged}
          showEstimatedDuration={this.state.showEstimatedDuration}
          suggestedDurations={finalSuggestedDurations}
          onChangeToSuggestedTime={this.handleSetMemoryDuration}
          onToggleEstimatedDuration={this.handleToggleEstimatedTime}
          adjustHourDraftDuration={this.props.adjustHourDraftDuration}
          scrollTop={this.state.scrollTop}
          scrollOffset={this.state.scrollOffset}
          weeklyCapacityDetails={weeklyCapacityDetails}
          isTimerDisabled={ui.isTimerDisabled}
          isTimerHidden={ui.isTimerHidden}
          onToggleTimer={this.props.toggleTimerOnHourDraft}
          isTimerRunning={ui.isTimerRunning}
          isSaveDisabled={ui.isSaveDisabled}
        />
      </div>
    );
    if (showHint) {
      return (
        <OnboardingHintPopover
          position="bottom"
          isOpen
          target={section}
          onConfirm={() => this.props.saveUserProperty(HAS_ADDED_TIME_TO_HOUR)}
          hintKey={HAS_ADDED_TIME_TO_HOUR}
          ignoreTargetOnClick={false}
        />
      );
    }
    return section;
  };

  showUpgradeModal = () => {
    this.props.showSubscriptionNoticeModal('featureHourStatesRequiresUpgrade');
  };

  handleScroll = ({ currentTarget }: React.MouseEvent<HTMLElement>) => {
    if (currentTarget.scrollTop !== this.state.scrollTop) {
      this.setState({ scrollTop: currentTarget.scrollTop });
    }
  };

  handleIsDragging = (isDragging: boolean) => {
    if (this.state.isDragging !== isDragging) {
      this.setState({ isDragging });
    }
  };

  handleClearDraft = () => {
    this.props.clearHourDraft();
    this.props.clearHourHistory();
  };

  handleTimestampChange = (index: number, modifiedTimestamp: HourTimestamp) => {
    this.props.setHourDraftTimestamp(index, modifiedTimestamp);
  };

  handleTimestampClear = (indexToRemove: number) => {
    const timestamps = [...this.props.draft.timestamps];

    if (timestamps.length === 1) {
      // TODO: Look closer at this, seems useless vs passing [] (in TIC context, at least)
      this.props.toggleTimestampOnHourDraft();
    } else {
      timestamps.splice(indexToRemove, 1);
      this.props.updateHourDraft({ timestamps });
    }
  };

  handleTimestampCreate = (newTimestamp: HourTimestamp) => {
    if (newTimestamp.from && newTimestamp.to) {
      this.props.setHourDraftTimestamp(null, newTimestamp);
      this.setState({ pendingTimestamp: null });
    } else {
      this.setState({ pendingTimestamp: newTimestamp });
    }
  };

  handleShortcuts = (action: string) => {
    switch (action) {
      case CLOSE_STANDALONE_HOUR_EDITOR:
        this.handleClearDraft();
        break;
      default:
        break;
    }
  };

  handleChangeLinks = (links: Array<ExternalHourLink>) => {
    this.props.updateHourDraft({
      external_links: links,
    });
  };

  renderHourStateDropdown = () => {
    const { draft, ui, showHourStateDropdown, showUpgradeModalForHourStateDropdown } = this.props;
    const isEntryOrDayLocked = ui.isLocked || ui.isDayLocked;

    if (showUpgradeModalForHourStateDropdown) {
      return (
        <FeatureGatedButton
          isFeatureLocked
          priority="xlow"
          size="xsmall"
          disabled={isEntryOrDayLocked}
          onClickLocked={this.showUpgradeModal}
          data-testid="time_entry_state"
        >
          State
        </FeatureGatedButton>
      );
    }
    if (showHourStateDropdown) {
      return (
        <Tooltip tooltip="Select time entry state" key="state-select">
          <HourStateDropdown
            onChange={this.handleChangeState}
            disableBillable={!draft.billable}
            disabled={isEntryOrDayLocked}
            filterMode={false}
            showNoState
          >
            <Button
              priority="xlow"
              size="xsmall"
              disabled={isEntryOrDayLocked}
              data-testid="time_entry_state"
            >
              {<Icon name="Contrast01" type="solid" />}
              State
            </Button>
          </HourStateDropdown>
        </Tooltip>
      );
    }

    return null;
  };

  renderForm = () => {
    const {
      draft,
      ui,
      tags,
      allTags,
      requiredTagIds,
      standalone,
      userTimeZone,
      defaultTagIds,
      hasTicIntegrationsSupport,
      hasAnyConnectionWithReferenceTasks,
    } = this.props;
    const isEntryOrDayLocked = ui.isLocked || ui.isDayLocked;
    const wrapSurface = (node: React.ReactElement) => <Surface surface={1}>{node}</Surface>;
    const showSelectedHourStateSection = (draft.state?.id ?? 0) > 0;
    const showStateSelectionStandaloneButton = !showSelectedHourStateSection;
    const autofillEnabled =
      ui.timelineHasMemoriesWithinTimestampRanges && this.state.canRequestGeneratedNote;
    const autofillTooltip =
      !autofillEnabled && !this.state.canRequestGeneratedNote
        ? 'Autofill is disabled until you make changes to your timesheet'
        : !autofillEnabled
          ? 'There are no memories within the selected timespan'
          : 'Autofill with AI or use titles from selected memories';

    return wrapSurface(
      <form ref={this.hourFormRef} onSubmit={this.handleSubmitForm} autoComplete="off">
        {/* insure that implicit submission works as expected - ugly hack for now, switch to hidden
        prop once supported in all recent versions of Safari */}
        <input type="submit" style={{ position: 'absolute', left: -9999 }} tabIndex={-1} />
        <InfoMessagesSection ui={ui} standalone={standalone} onUnlock={this.handleUnlock} />
        {/* Connected component that deals with everything project related */}
        <NoteSection
          autoFocus={window.innerWidth >= MOBILE_BREAKPOINT}
          onSubmit={this.handleSubmitNoteSection}
          standalone={standalone}
          disabled={isEntryOrDayLocked}
          disableAutofill={!autofillEnabled}
          required={ui.isMissingRequiredNote}
          note={this.state.note}
          onChange={this.handleNoteChange}
          autoNoteStrategy={ui.autoNoteStrategy}
          defaultAutoNoteStrategy={ui.defaultAutoNoteStrategy}
          onAutoNoteStrategyChange={this.handleAutoNoteStrategyChange}
          placeholder="Add a note for this work"
          hideAutofill={ui.isLocked || ui.isDayLocked || ui.isActingAsOtherUser} //  || ui.hasEnabledTic
          autoFillTooltip={autofillTooltip}
          hasEnabledTic={ui.hasEnabledTic}
          timestampsHaveMemories={ui.hasMemoriesInTimestamps}
        />
        <div style={{ paddingBottom: !ui.showTags ? 10 : 0 }}>
          <ProjectSection
            forecastId={draft?.forecast_id}
            hasSelectedProject={ui.hasSelectedProject}
            standalone={standalone}
            disabled={isEntryOrDayLocked || !ui.isProjectEditEnabled}
            noBorder={!ui.showTags}
            maxHeight={!ui.hasMemoryOrLinkedMemories && !ui.showMemories ? 250 : undefined}
          />
        </div>
        {(ui.hasSelectedProject && ui.showTags && (
          <TagSection
            standalone={standalone}
            requiredTagIds={requiredTagIds}
            allowChanges={ui.canUserManageTags}
            tags={tags}
            allTags={allTags}
            disabled={isEntryOrDayLocked}
            selected={draft.label_ids}
            onChange={this.props.setHourDraftTags}
            accountId={ui.accountId}
            maxHeight={!ui.hasMemoryOrLinkedMemories && !ui.showMemories ? 185 : 300}
            defaultTagIds={defaultTagIds}
            singleTagMode={!!draft.project.fullProject?.allow_only_one_tag}
          />
        )) ||
          null}

        <TimeSectionScrollSync
          scrollOffset={this.state.scrollOffset}
          enabled={!standalone}
          scrollTop={this.state.scrollTop}
        >
          {this.renderTimeSection()}
        </TimeSectionScrollSync>

        {ui.showMemoryDrawer && (
          <MemoryDrawer
            date={draft.day}
            timeFormat={ui.timeFormat}
            snapToMinutes={ui.snapToMinutes}
            onTimestampDelete={this.handleTimestampClear}
            disableEditing={isEntryOrDayLocked}
          />
        )}
        {hasTicIntegrationsSupport && hasAnyConnectionWithReferenceTasks && (
          <Suspense>
            <Section style={{ margin: '10px 0px 0px', padding: '0 12px 10px' }}>
              <TicTasksInHourEditor
                date={draft.day}
                lastSelectedMemory={draft._lastDirectlySelectedMemory}
                links={draft.external_links}
                onChangeLinks={this.handleChangeLinks}
                disabled={isEntryOrDayLocked}
              />
            </Section>
          </Suspense>
        )}

        {ui.showTimestamp && (
          <div>
            {draft.timestamps.map((timestamp, index) => (
              <TimestampPickerSection
                key={index}
                standalone={standalone}
                disabled={isEntryOrDayLocked}
                timeFormat={ui.timeFormat}
                snapToMinutes={ui.snapToMinutes}
                isTimerRunning={ui.isTimerRunning}
                showInput
                onChange={change => this.handleTimestampChange(index, change)}
                timestamp={timestamp}
                date={draft.day}
                onClear={() => this.handleTimestampClear(index)}
                userTimeZone={userTimeZone}
              />
            ))}

            {!ui.isLocked && !ui.isDayLocked && this.renderTimestampCreation()}
          </div>
        )}
        {showSelectedHourStateSection && (
          <SelectedHourStateSection
            selectedStateId={draft.state?.id!}
            states={this.props.states}
            disableBillable={!draft.billable}
            onChangeState={this.handleChangeState}
            disabled={isEntryOrDayLocked}
          />
        )}
        <ActionSection
          standalone={standalone}
          disabled={isEntryOrDayLocked}
          isSaveDisabled={ui.isSaveDisabled}
          isActingAsOtherUser={ui.isActingAsOtherUser}
          date={draft.day}
          onToggleTimestamp={this.props.toggleTimestampOnHourDraft}
          onToggleEstimatedDuration={this.handleToggleEstimatedTime}
          onToggleHourHistoryModal={this.props.toggleHourHistoryModal}
          onShowCopyModal={this.handleShowCopyModal}
          onShowMoveModal={this.handleShowMoveModal}
          onToggleSubscriptionNoticeModal={this.props.showSubscriptionNoticeModal}
          showTimestamp={ui.showTimestamp}
          isEstimatedDurationUsed={this.state.showEstimatedDuration}
          isBasedOnSuggestion={ui.isBasedOnSuggestion}
          isCopyDisabled={ui.isCopyDisabled}
          isMoveDisabled={ui.isMoveDisabled}
          isAddPlannedTimeDisbled={ui.isAddPlannedTimeDisbled}
          isAuditFeatureEnabled={ui.isAuditFeatureEnabled}
          isNewDraft={!draft.id}
          onToggleBillable={this.handleToggleBillable}
          isBillable={this.props.draft.billable}
          isProjectBillable={!!this.props.draft.project.fullProject?.billable}
          showBillableChangedWarning={this.state.showBillableChangedWarning}
          onCloseBillableChangedWarning={this.handleCloseBillableChangedWarning}
          style={{ paddingTop: 10 }}
          hourStateButtonElement={
            showStateSelectionStandaloneButton && this.renderHourStateDropdown()
          }
        />

        <ButtonSection
          isVisible={standalone || ui.isActingAsOtherUser}
          standalone
          isHourSaved={!!draft.id}
          ui={ui}
          isTimerRunning={ui.isTimerRunning}
          onRejectSuggestion={this.handleRejectSuggestion}
          onSubmit={this.handleSubmitForm}
          onSubmitAndComplete={this.handleSubmitAndCompleteForm}
          onCancel={this.handleClearDraft}
          onDelete={this.props.deleteHourDraft}
          stateId={this.props.draft.state?.id ?? null}
          states={this.props.states}
          hasHourStatesFeature={this.props.hasHourStatesFeature}
        />
      </form>
    );
  };

  renderTimestampCreation() {
    const { draft, ui, standalone, userTimeZone } = this.props;
    const showNewTimeslotButton = !standalone && !ui.showMemories;

    return this.state.pendingTimestamp ? (
      <TimestampPickerSection
        timestamp={this.state.pendingTimestamp}
        standalone={standalone}
        disabled={false}
        timeFormat={ui.timeFormat}
        snapToMinutes={ui.snapToMinutes}
        isTimerRunning={false}
        showInput
        onChange={this.handleTimestampCreate}
        date={draft.day}
        onClear={() => this.setState({ pendingTimestamp: null })}
        userTimeZone={userTimeZone}
      />
    ) : (
      showNewTimeslotButton && (
        <div className={styles.timestampsActions}>
          <Button
            priority="medium"
            onClick={() => this.setState({ pendingTimestamp: { from: '', to: '', entry_ids: [] } })}
          >
            <FontAwesome name="plus" />
            <span>Timestamps</span>
          </Button>
        </div>
      )
    );
  }

  render() {
    const { draft, ui, standalone, isRouteCalendar } = this.props;
    // It's unclear how HourEditor can exist without a draft, possibly a race condition? This _should_ fix issues like...
    // * https://sentry.io/organizations/timely-as/issues/959389870/?project=114853&query=is%3Aunresolved&statsPeriod=14d
    // * https://sentry.io/organizations/timely-as/issues/1065035951/?project=114853&query=is%3Aunresolved&statsPeriod=14d
    const editorHasInvalidDraft = !draft || !draft.project || !getProjectColor(draft.project.base);

    if (editorHasInvalidDraft) {
      return null;
    }

    const memorySectionDefaultHeight =
      (ui.hasInstalledMemory && ui.hasMemories) || !ui.hasInstalledMemory
        ? 125
        : MEMORY_GROUP_HEADER_HEIGHT;

    const containerClass = classNames(styles.container, {
      [styles.standalone]: standalone,
      [styles.hasEnabledTic]: standalone && ui.hasEnabledTic,
      [styles.standaloneModal]: ui.isActingAsOtherUser,
      [styles.noMemories]: !ui.showMemories,
    });

    return (
      <div className={containerClass}>
        {standalone && (
          <Hotkeys shortcuts={keymap.hourEditor.shortcuts} handler={this.handleShortcuts} />
        )}
        {standalone || ui.isActingAsOtherUser ? (
          <>
            {this.renderForm()}
            {!ui.isActingAsOtherUser && !ui.hasEnabledTic && (
              <>
                <MemorySectionHeader
                  duration={this.props.selectedMemoriesDuration}
                  linkedMemoriesCount={draft.entry_ids.length}
                  hasInstalledMemory={ui.hasMemoryOrLinkedMemories}
                  ui={ui}
                  onToggleMemories={this.handleToggleMemories}
                  standalone={standalone}
                />
                <Scrollbars
                  className={styles.standaloneScrollbarContainer}
                  onScroll={this.handleScroll}
                  autoHeight
                  autoHeightMin={150}
                  autoHeightMax={340}
                >
                  {ui.hasMemoryOrLinkedMemories && (
                    <MemorySection
                      date={draft.day}
                      linkedMemoriesCount={draft.entry_ids.length}
                      standalone={standalone}
                      ui={ui}
                      showDebugInfo={ui.showDebugInfo}
                      scrollTop={this.state.scrollTop + 10}
                    />
                  )}
                </Scrollbars>
              </>
            )}
          </>
        ) : (
          <Scrollbars
            hideTracksWhenNotNeeded
            onScroll={this.handleScroll}
            className={styles.scrollbars}
            style={{
              // measure the height manually because we don't want to be scrollable
              height:
                !ui.showMemories || !isRouteCalendar
                  ? memorySectionDefaultHeight + this.state.scrollOffset
                  : '100%',
            }}
          >
            <div
              style={{ position: 'relative' }}
              className={classNames({ [styles.noMemoriesWrap]: !draft.entry_ids.length })}
            >
              <FormWrap
                enabled={!standalone}
                scrollOffset={
                  this.state.scrollOffset - (this.timeSectionRef.current?.clientHeight || 0)
                }
                height={this.state.scrollOffset}
                scrollTop={this.state.scrollTop}
              >
                {this.renderForm()}
              </FormWrap>

              {!ui.hasEnabledTic && (
                <>
                  <MemorySectionHeader
                    duration={this.props.selectedMemoriesDuration}
                    linkedMemoriesCount={draft.entry_ids.length}
                    hasInstalledMemory={ui.hasMemoryOrLinkedMemories}
                    ui={ui}
                    onToggleMemories={this.handleToggleMemories}
                    standalone={standalone}
                  />

                  {ui.showMemorySection && (
                    <MemorySection
                      linkedMemoriesCount={draft.entry_ids.length}
                      date={draft.day}
                      ui={ui}
                      standalone={standalone}
                      showDebugInfo={ui.showDebugInfo}
                      scrollTop={this.state.scrollTop}
                      setIsDragging={this.handleIsDragging}
                    />
                  )}
                </>
              )}

              {!ui.hasMemoryOrLinkedMemories && (
                <ButtonSection
                  isVisible
                  standalone={false}
                  isHourSaved={!!draft.id}
                  ui={ui}
                  isTimerRunning={ui.isTimerRunning}
                  onRejectSuggestion={this.handleRejectSuggestion}
                  onSubmit={this.handleSubmitForm}
                  onSubmitAndComplete={this.handleSubmitAndCompleteForm}
                  onCancel={this.handleClearDraft}
                  onDelete={this.props.deleteHourDraft}
                  stateId={this.props.draft.state?.id ?? null}
                  states={this.props.states}
                  hasHourStatesFeature={this.props.hasHourStatesFeature}
                />
              )}
            </div>
          </Scrollbars>
        )}
        {!standalone && !ui.isActingAsOtherUser && ui.hasMemoryOrLinkedMemories ? (
          <ButtonSection
            isVisible={!this.state.isDragging}
            standalone={false}
            isHourSaved={!!draft.id}
            ui={ui}
            isTimerRunning={ui.isTimerRunning}
            onRejectSuggestion={this.handleRejectSuggestion}
            onSubmit={this.handleSubmitForm}
            onSubmitAndComplete={this.handleSubmitAndCompleteForm}
            onCancel={this.handleClearDraft}
            onDelete={this.props.deleteHourDraft}
            stateId={this.props.draft.state?.id ?? null}
            states={this.props.states}
            hasHourStatesFeature={this.props.hasHourStatesFeature}
          />
        ) : null}
      </div>
    );
  }
}

export { HourEditor };

export default connect(mapStateToProps, mapActionsToProps)(HourEditor);
